import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getSignedMandates } from '../../api/documents';

type SignedMandate = string[];

interface SignedMandatesState {
  signedMandates: SignedMandate;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const initialState: SignedMandatesState = {
  signedMandates: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const fetchSignedMandates = createAsyncThunk<
  SignedMandate,
  number,
  { state: { signedMandates: SignedMandatesState }; rejectValue: string }
>('signedMandates/fetchSignedMandates', async (company_id, { getState, rejectWithValue }) => {
  const { signedMandates, lastFetched } = getState().signedMandates;

  if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
    return signedMandates;
  }

  try {
    const mandatesData: SignedMandate = await getSignedMandates(company_id);
    return mandatesData;
  } catch (err) {
    if (err instanceof Error) {
      console.error('Error fetching signed mandates:', err.message);
      return rejectWithValue(err.message);
    } else {
      console.error('Unknown error fetching signed mandates');
      return rejectWithValue('An unknown error occurred');
    }
  }
});

const signedMandatesSlice = createSlice({
  name: 'signedMandates',
  initialState,
  reducers: {
    resetSignedMandatesState: (state) => {
      state.signedMandates = [];
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSignedMandates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSignedMandates.fulfilled, (state, action: PayloadAction<SignedMandate>) => {
        state.loading = false;
        state.signedMandates = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchSignedMandates.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'An unknown error occurred';
        console.error('Error in state:', state.error);
      });
  },
});

export const { resetSignedMandatesState } = signedMandatesSlice.actions;
export default signedMandatesSlice.reducer;
